import { useCallback, useEffect, useState } from 'react'
import {
  PhotoVerificationAttemptDetailDto,
  PhotoVerificationAttemptListItemDto,
} from '@ivy-monorepo/shared'
import { useSearchParams } from 'react-router-dom'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import PhotoVerificationAttemptDetailModal from '../../components/modals/PhotoVerificationAttemptDetailModal'
import { toPhotoVerificationAttemptRow } from '../../helpers/photo-verification'
import { adminService } from '../../services/admin.service'

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 180
  const paginationHeight = 60
  const availableHeight = viewportHeight - headerHeight - paginationHeight
  return Math.max(10, Math.min(100, Math.floor(availableHeight / rowHeight)))
}

const PhotoVerificationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [attempts, setAttempts] = useState<PhotoVerificationAttemptListItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<PhotoVerificationAttemptDetailDto | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const page = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const pageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const search = searchParams.get('search') || ''

  const loadAttempts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await adminService.getPhotoVerificationAttempts(page, pageSize, search)
      setAttempts(response.attempts)
      setPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch photo verification attempts', err)
      setError('Failed to load photo verification attempts.')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, search])

  useEffect(() => {
    loadAttempts()
  }, [loadAttempts])

  const handlePageChange = (nextPage: number) => {
    const next = new URLSearchParams(searchParams)
    next.set('page', nextPage.toString())
    next.set('pageSize', pageSize.toString())
    setSearchParams(next)
  }

  const openDetail = async (attempt: PhotoVerificationAttemptListItemDto) => {
    setDetailLoading(true)
    setActionError(null)
    setSelected(null)
    try {
      const detail = await adminService.getPhotoVerificationAttempt(attempt.publicId)
      setSelected(detail)
    } catch (err) {
      console.log('Failed to fetch photo verification attempt detail', err)
      setError('Failed to load attempt details.')
    } finally {
      setDetailLoading(false)
    }
  }

  const closeDetail = () => {
    setSelected(null)
    setActionError(null)
  }

  const handleManualVerify = async () => {
    if (!selected?.canManuallyVerify) return
    setVerifying(true)
    setActionError(null)
    try {
      await adminService.manuallyVerifyPhoto(selected.publicId)
      closeDetail()
      await loadAttempts()
    } catch (err: any) {
      const message =
        err?.response?.data?.userMessage
        || err?.response?.data?.message
        || 'Failed to manually verify this photo.'
      setActionError(message)
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="photo-verification-page">
      <div className="photo-verification-header">
        <div>
          <h2>Photo verification</h2>
          <p className="photo-verification-subtitle">
            Failed Google Vision checks. Manual verify is only available when the attempt still matches the user’s current avatar.
          </p>
        </div>
      </div>

      {error && <div className="photo-verification-error">{error}</div>}

      <Table
        id="photo-verification-attempts"
        name="Failed photo checks"
        keyField="publicId"
        initialSortColumn="createdAt"
        columns={[
          { label: '', accessor: 'currentImageUrl', type: 'avatar' },
          { label: 'Name', accessor: 'fullName', type: 'string' },
          { label: 'Email', accessor: 'email', type: 'string' },
          { label: 'Context', accessor: 'context', type: 'string' },
          { label: 'Failure', accessor: 'statusLabel', type: 'string' },
          { label: 'Current avatar', accessor: 'isCurrentAvatarLabel', type: 'string' },
          { label: 'Can verify', accessor: 'canManuallyVerifyLabel', type: 'string' },
          { label: 'Created', accessor: 'createdAt', type: 'datetime' },
        ]}
        sourceData={attempts.map(toPhotoVerificationAttemptRow)}
        isPropertyBarVisible={false}
        filterTerms=""
        onRowClick={(row: { publicId: string }) => {
          const attempt = attempts.find(item => item.publicId === row.publicId)
          if (attempt) openDetail(attempt)
        }}
      />

      {loading && attempts.length === 0 && <p>Loading…</p>}

      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <PhotoVerificationAttemptDetailModal
        selected={selected}
        detailLoading={detailLoading}
        verifying={verifying}
        actionError={actionError}
        onClose={closeDetail}
        onManualVerify={handleManualVerify}
      />
    </div>
  )
}

export default PhotoVerificationPage
